import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AVAILABLE_FOLDERS } from '../utils/documentUtils';
import { documentService } from '../services/documentService';
import { Document } from '../types/Document';
import DocumentCard from '../components/DocumentCard';

const DocumentsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const documentsPerPage = 12;

  // Get folder and page from URL params, with defaults
  const selectedFolder = searchParams.get('folder') || '001';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Validate folder and page parameters
  const validFolder = AVAILABLE_FOLDERS.includes(selectedFolder) ? selectedFolder : '001';
  const validPage = Math.max(1, currentPage);

  useEffect(() => {
    const loadDocuments = async () => {
      setLoading(true);
      try {
        const folderDocs = await documentService.getDocumentsByFolder(validFolder);
        setDocuments(folderDocs);
      } catch (error) {
        console.error('Error loading documents:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDocuments();
  }, [validFolder]);

  // Calculate pagination with validated page
  const totalPages = Math.ceil(documents.length / documentsPerPage);
  const finalPage = Math.min(validPage, totalPages || 1);

  // Update page title based on current folder and page
  useEffect(() => {
    document.title = `Folder ${validFolder}, Page ${finalPage} - Epstein Files - Democracy Renaissance`;
    return () => {
      document.title = 'Democracy Renaissance';
    };
  }, [validFolder, finalPage]);
  const startIndex = (finalPage - 1) * documentsPerPage;
  const currentDocuments = documents.slice(startIndex, startIndex + documentsPerPage);

  const updateURLParams = (folder: string, page: number = 1) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('folder', folder);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const handleFolderChange = (folder: string) => {
    updateURLParams(folder, 1); // Reset to page 1 when changing folders
  };

  const goToPage = (page: number) => {
    updateURLParams(validFolder, page);
    // Scroll to top of document grid
    setTimeout(() => {
      document.getElementById('documents-grid')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="container">
      <div className="card patriotic-accent">
        <h1>Epstein Files Document Browser</h1>
        <p>
          Browse through {new Intl.NumberFormat().format(22689)} processed documents 
          organized in {AVAILABLE_FOLDERS.length} folders. Each document includes both 
          the original page image and searchable extracted text from Tesseract OCR processing.
        </p>
      </div>

      <div className="card">
        <h2>Select Document Folder</h2>
        <p>Choose a folder to browse House Oversight Committee documents:</p>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
          gap: 'var(--space-md)', 
          marginTop: 'var(--space-lg)' 
        }}>
          {AVAILABLE_FOLDERS.map(folder => (
            <button
              key={folder}
              onClick={() => handleFolderChange(folder)}
              className={`btn ${validFolder === folder ? '' : 'btn-secondary'}`}
              style={{ 
                padding: 'var(--space-md)',
                textAlign: 'center',
                border: validFolder === folder ? '2px solid var(--democracy-blue)' : 'none',
                position: 'relative'
              }}
            >
              📁 Folder {folder}
              {validFolder === folder && (
                <div style={{
                  position: 'absolute',
                  top: '-2px',
                  left: '-2px',
                  right: '-2px',
                  height: '3px',
                  background: 'linear-gradient(90deg, var(--democracy-red) 0%, var(--democracy-white) 50%, var(--democracy-blue) 100%)',
                  borderRadius: '16px 16px 0 0'
                }} />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 'var(--space-lg)',
          flexWrap: 'wrap',
          gap: 'var(--space-md)'
        }}>
          <h2>Folder {validFolder} Documents</h2>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 'var(--space-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'rgba(26, 31, 58, 0.7)'
          }}>
            {loading ? (
              <span>Loading...</span>
            ) : (
              <>
                <span>📄 {documents.length} documents found</span>
                <span>•</span>
                <span>Page {finalPage} of {totalPages}</span>
              </>
            )}
          </div>
        </div>

        {loading ? (
          <div style={{ 
            textAlign: 'center', 
            padding: 'var(--space-2xl)',
            color: 'rgba(26, 31, 58, 0.6)'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: 'var(--space-md)' }}>📄</div>
            <p>Loading documents from folder {validFolder}...</p>
          </div>
        ) : (
          <>
            <div 
              id="documents-grid"
              className="document-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: 'var(--space-lg)',
                marginBottom: 'var(--space-xl)'
              }}
            >
              {currentDocuments.map(document => (
                <DocumentCard 
                  key={document.id} 
                  document={document}
                  showPreview={false}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 'var(--space-sm)',
                marginTop: 'var(--space-xl)',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => goToPage(finalPage - 1)}
                  disabled={finalPage === 1}
                  className="btn btn-secondary"
                  style={{ 
                    fontSize: 'var(--font-size-sm)',
                    padding: 'var(--space-xs) var(--space-md)'
                  }}
                >
                  ← Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`btn ${finalPage === pageNum ? '' : 'btn-secondary'}`}
                      style={{ 
                        fontSize: 'var(--font-size-sm)',
                        padding: 'var(--space-xs) var(--space-md)',
                        minWidth: '40px'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {totalPages > 5 && (
                  <>
                    <span style={{ color: 'rgba(26, 31, 58, 0.6)' }}>...</span>
                    <button
                      onClick={() => goToPage(totalPages)}
                      className={`btn ${finalPage === totalPages ? '' : 'btn-secondary'}`}
                      style={{ 
                        fontSize: 'var(--font-size-sm)',
                        padding: 'var(--space-xs) var(--space-md)'
                      }}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => goToPage(finalPage + 1)}
                  disabled={finalPage === totalPages}
                  className="btn btn-secondary"
                  style={{ 
                    fontSize: 'var(--font-size-sm)',
                    padding: 'var(--space-xs) var(--space-md)'
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <h2>Document Browser Actions</h2>
        <div style={{ 
          display: 'flex', 
          gap: 'var(--space-md)', 
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          <button 
            onClick={() => navigator.share?.({ 
              title: `Epstein Files - Folder ${validFolder}, Page ${finalPage}`, 
              url: window.location.href 
            })}
            className="btn btn-secondary"
          >
            🔗 Share This Page
          </button>
          <Link to="/" className="btn btn-secondary">
            🏠 Back to Home
          </Link>
        </div>
        
        <div style={{
          marginTop: 'var(--space-lg)',
          padding: 'var(--space-md)',
          backgroundColor: 'var(--democracy-light-blue)',
          borderRadius: '12px',
          fontSize: 'var(--font-size-sm)',
          color: 'rgba(26, 31, 58, 0.8)'
        }}>
          <h3 style={{ color: 'var(--democracy-blue)', marginBottom: 'var(--space-xs)' }}>
            💡 Browsing Tips
          </h3>
          <ul style={{ margin: 0, paddingLeft: 'var(--space-lg)' }}>
            <li><strong>View Full:</strong> See complete document with image and extracted text</li>
            <li><strong>Image:</strong> Open original document image in new tab</li>
            <li><strong>Text:</strong> View raw OCR text content</li>
            <li><strong>Search:</strong> Use the search function for specific terms across all documents</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DocumentsPage;